import { useState } from "react";
import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import { IoCloseOutline } from "react-icons/io5";
import IconButton from "@mui/material/IconButton";
import DialogContent from "@mui/material/DialogContent";
import TextField from "@mui/material/TextField";
import DialogActions from "@mui/material/DialogActions";
import InputAdornment from "@mui/material/InputAdornment";
import { motion } from "motion/react";
import { IoSend } from "react-icons/io5";
import ChatMessage from "./ChatMessage";
import { sendQuery } from "../utils/api";
import { useCurrentChatSession } from "../utils/useCurrentSession";
import ScrollToBottom from "react-scroll-to-bottom";

export const ChatbotDialog = ({ isOpen, setIsChatbotDialogOpen }) => {
  const { sessionId, messages, setMessages } = useCurrentChatSession();
  const [newMessage, setNewMessage] = useState("");
  
  // Check if any message is currently being rendered
  const isMessageRendering = messages.some(message => 
    message.role === "assistant" && !message.isRendered
  );

  const handleSendMessage = async () => {
    // Don't allow sending if a message is currently being rendered
    if (isMessageRendering) {
      return;
    }
    
    if (newMessage.trim()) {
      const userMessage = {
        role: "user",
        content: newMessage,
        isRendered: false,
      };
      const updatedMessages = [...messages, userMessage];
      setMessages(updatedMessages);
      setNewMessage("");

      handleAiResponse(
        { query: newMessage, sessionId: sessionId },
        updatedMessages
      );
    }
  };

  const handleAiResponse = async (body, currentMessages) => {
    const response = await sendQuery(body);
    if (response) {
      const aiMessage = { ...response, isRendered: false };
      const updatedMessages = [...currentMessages, aiMessage];
      setMessages(updatedMessages);
    }
  };

  return (
    <div>
      <Dialog
        open={isOpen}
        onClose={() => setIsChatbotDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: { borderRadius: 10, backgroundColor: "var(--shade-1)" },
        }}
      >
        <DialogTitle
          sx={{ m: 0, p: 2 }}
          id="customized-dialog-title"
          className="bg-shade-1 text-shade-4 text-center !text-md md:!text-3xl"
        >
          Ask anything about me...
          <IconButton
            aria-label="close"
            onClick={() => setIsChatbotDialogOpen(false)}
            sx={(theme) => ({
              position: "absolute",
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <IoCloseOutline />
          </IconButton>
        </DialogTitle>

        <ScrollToBottom
          className="h-[50vh]"
          mode="bottom"
          followButtonClassName="hidden"
        >
          <DialogContent className="bg-shade-1  p-0">
            <ChatMessage message={messages} setMessage={setMessages} />
          </DialogContent>
        </ScrollToBottom>
        <DialogActions className="bg-shade-1">
          <div className="flex flex-row items-center justify-between w-full border border-shade-2 rounded-xl p-1">
            <TextField
              fullWidth
              autoFocus
              placeholder="Ask me anything..."
              variant="outlined"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === "Enter" && !isMessageRendering) {
                  handleSendMessage();
                }
              }}
              InputLabelProps={{
                shrink: false,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <motion.button
                      whileHover={!isMessageRendering ? { scale: 1.2 } : {}}
                      whileTap={!isMessageRendering ? { scale: 0.9 } : {}}
                      className={`p-4 rounded-full ${isMessageRendering ? '!bg-gray-400 cursor-not-allowed' : '!bg-main'}`}
                      onClick={() => handleSendMessage()}
                      disabled={isMessageRendering}
                    >
                      <IoSend className="size-5 text-white" />
                    </motion.button>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    border: "none",
                  },
                  "&:hover fieldset": {
                    border: "none",
                  },
                  "&.Mui-focused fieldset": {
                    border: "none",
                  },
                  "& .MuiInputBase-input": {
                    color: "var(--shade-4)",
                    "&::placeholder": {
                      color: "var(--shade-4)",
                      opacity: 1,
                    },
                  },
                },
              }}
            />
          </div>
        </DialogActions>
      </Dialog>
    </div>
  );
};
